/**
 * Supplier catalog reads and writes (BLU-1268), wrapping `trpc.supplierCatalog.*`.
 *
 * - The supplier list, a supplier's style list and one style's variants are
 *   platform data that changes when Blue Monk imports a CSV: kept 5 minutes,
 *   never refetched on focus. Read for a location, they also carry what the
 *   org already has there, so they are kept 30 seconds (like the code lookup).
 * - Link and Add to my suppliers refresh the catalog list (its "Yours?"
 *   column) and the org's supplier list (the new or linked row), awaited, so
 *   a caller's `mutateAsync` resolves onto fresh data (DESIGN.md section 12.2).
 * - Add from catalog refreshes Base garments (its list and code tables), the
 *   style list, the opened style and the code lookup.
 * - `retry: false` on every write: each refusal is final.
 * - The AS Colour sync's status (platform admins only) is polled every 5 s
 *   while a run is going and not at all otherwise; when a run ends the
 *   catalog lists are refreshed.
 */
import { useEffect, useRef } from 'react';
import { useRole } from '@/hooks/use-role';
import { trpc } from '@/lib/trpc';

const CATALOG_STALE_MS = 5 * 60_000;
const SYNC_POLL_MS = 5_000;
/** Reads that carry the org's own rows (what is already added, a code's stock type) go stale sooner. */
const ORG_STALE_MS = 30_000;

export function useCatalogSuppliers(enabled = true) {
  return trpc.supplierCatalog.listSuppliers.useQuery(undefined, {
    enabled,
    staleTime: CATALOG_STALE_MS,
    refetchOnWindowFocus: false,
  });
}

/**
 * One catalog supplier's style list: no variants, their colour, size and
 * variant counts only. With a `locationId` (an owned floor), every style also
 * says whether the org already has it there (`addedAtLocation`); that half is
 * the org's own data, so it goes stale sooner.
 */
export function useCatalogStyles(catalogSupplierId: string | null, locationId: string | null = null) {
  return trpc.supplierCatalog.getStyles.useQuery(
    { catalogSupplierId: catalogSupplierId ?? '', ...(locationId ? { locationId } : {}) },
    {
      enabled: !!catalogSupplierId,
      staleTime: locationId ? ORG_STALE_MS : CATALOG_STALE_MS,
      refetchOnWindowFocus: false,
    },
  );
}

/**
 * One style with its variants, read when the picker opens it. With a
 * `locationId`, every variant also says whether the org already has it there.
 */
export function useCatalogStyle(catalogStyleId: string | null, locationId: string | null = null) {
  return trpc.supplierCatalog.getStyle.useQuery(
    { catalogStyleId: catalogStyleId ?? '', ...(locationId ? { locationId } : {}) },
    {
      enabled: !!catalogStyleId,
      staleTime: locationId ? ORG_STALE_MS : CATALOG_STALE_MS,
      refetchOnWindowFocus: false,
    },
  );
}

/**
 * The org's own row for a product code (does it exist, and for which stock
 * type), read before Add so the Stock type pick can be locked to it. Needs
 * `skus.manage`: without it the call is never made. `code` must already be a
 * valid product code; the caller debounces it.
 */
export function useCatalogCodeLookup(code: string, locationId: string | null, enabled: boolean) {
  const { can } = useRole();
  return trpc.supplierCatalog.lookupCode.useQuery(
    { code, locationId: locationId ?? '' },
    {
      enabled: enabled && can('skus.manage') && !!locationId && code !== '',
      staleTime: ORG_STALE_MS,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );
}

/** Link one of the org's suppliers to a catalog supplier, or create the org's supplier from it. */
export function useCatalogLinkMutations() {
  const utils = trpc.useUtils();
  const refresh = () =>
    Promise.all([utils.supplierCatalog.listSuppliers.invalidate(), utils.suppliers.list.invalidate()]);
  const linkSupplier = trpc.supplierCatalog.linkSupplier.useMutation({ retry: false, onSuccess: refresh });
  const addToMySuppliers = trpc.supplierCatalog.addToMySuppliers.useMutation({ retry: false, onSuccess: refresh });
  return { linkSupplier, addToMySuppliers };
}

/**
 * Add from catalog: the code, its blanks and their Supply at one location, in
 * one call. Refreshes Base garments, the style list and the opened style
 * (their "already added" ticks) and the code lookup (a new code now exists).
 */
export function useAddFromCatalog() {
  const utils = trpc.useUtils();
  return trpc.supplierCatalog.addToLocation.useMutation({
    retry: false,
    onSuccess: () =>
      Promise.all([
        utils.baseGarments.list.invalidate(),
        utils.baseGarments.codeTables.invalidate(),
        utils.baseGarments.linkStatus.invalidate(),
        utils.supplierCatalog.getStyles.invalidate(),
        utils.supplierCatalog.getStyle.invalidate(),
        utils.supplierCatalog.lookupCode.invalidate(),
      ]),
  });
}

/** The org's size and colour code tables, read only while the Add part of the picker shows. */
export function useOrgCodeTables(enabled: boolean) {
  return trpc.baseGarments.codeTables.useQuery(undefined, {
    enabled,
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * The AS Colour sync: is it set up, running, when it last synced, how its last
 * run ended. Platform admins only (the procedure refuses anyone else), so a
 * page that is not theirs passes `enabled` false. Polls only while a run goes;
 * when it ends, the supplier list, style lists and opened styles are re-read.
 */
export function useCatalogSyncStatus(enabled = true) {
  const utils = trpc.useUtils();
  const status = trpc.supplierCatalog.platform.catalogSyncStatus.useQuery(undefined, {
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => (query.state.data?.running ? SYNC_POLL_MS : false),
  });
  const running = status.data?.running ?? false;
  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !running) {
      void Promise.all([
        utils.supplierCatalog.listSuppliers.invalidate(),
        utils.supplierCatalog.getStyles.invalidate(),
        utils.supplierCatalog.getStyle.invalidate(),
      ]);
    }
    wasRunning.current = running;
  }, [running, utils]);
  return status;
}

/** Start a sync. The answer is immediate (started, running or not_configured); the status poll follows the run. */
export function useCatalogSyncNow() {
  const utils = trpc.useUtils();
  return trpc.supplierCatalog.platform.catalogSyncNow.useMutation({
    retry: false,
    onSuccess: () => utils.supplierCatalog.platform.catalogSyncStatus.invalidate(),
  });
}
